import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Lock, Maximize, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { devLog } from "@/lib/devLog";

/**
 * Kiosk mode — turns the bakery tablet into an app-only device.
 *
 * What we can/can't do as a web app:
 *   ✓ Take the page fullscreen so the address bar / system bars hide
 *     where the browser allows it (Chrome on Android does).
 *   ✓ Hold a Wake Lock so the screen does not sleep mid-shift.
 *   ✓ Watch fullscreenchange + visibilitychange and the moment the
 *     user escapes (e.g. Android swipe-up app-switcher), raise a
 *     full-screen blocking gate over the whole app. The operator
 *     cannot interact with anything underneath until either they
 *     tap "Resume kiosk" (re-enters fullscreen) OR an admin enters
 *     their PIN to exit kiosk.
 *   ✓ Warn on every navigation-away attempt via `beforeunload`.
 *   ✗ Cannot truly prevent the operator from force-killing Chrome or
 *     pulling down the Android notification shade — that needs a
 *     native kiosk app or Android Device Owner mode. We document this
 *     limitation in the unlock dialog.
 *
 * State lives in `localStorage` so a reload (or accidental navigation
 * the operator dismissed) snaps the tablet back into kiosk.
 */

const KIOSK_KEY = "ozbakery.kiosk.enabled";
const KioskCtx = createContext(null);

export function KioskProvider({ children }) {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(KIOSK_KEY) === "1";
    } catch {
      return false;
    }
  });
  // The "escape-attempt" gate. Raised when fullscreen drops or
  // visibility changes while kiosk is still enabled. While true,
  // nothing under the gate is reachable.
  const [gated, setGated] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const wakeLockRef = useRef(null);
  // Timestamp (ms epoch) until which gate-raising events are ignored.
  // Populated by call-sites that intentionally open a file picker /
  // camera intent / native print dialog — anything that legitimately
  // takes the tab out of fullscreen for a short window.
  const suspendUntilRef = useRef(0);
  // Debounce timers so a brief visibility flip (Android's camera intent
  // fires visibilitychange and fullscreenchange both, and returns in
  // 3-15s) doesn't lock the operator out. Fires setGated(true) only
  // when the tab is *still* hidden / outside fullscreen after the delay.
  const visTimerRef = useRef(null);
  const fsTimerRef = useRef(null);
  const GATE_DEBOUNCE_MS = 5000;

  const isSuspended = () => Date.now() < suspendUntilRef.current;
  const suspend = useCallback((ms = 60000) => {
    suspendUntilRef.current = Date.now() + Math.max(0, Number(ms) || 0);
  }, []);
  const clearGateTimers = () => {
    if (visTimerRef.current) { clearTimeout(visTimerRef.current); visTimerRef.current = null; }
    if (fsTimerRef.current) { clearTimeout(fsTimerRef.current); fsTimerRef.current = null; }
  };

  // ------------- Persist + side-effects ------------------------------
  useEffect(() => {
    try {
      if (enabled) localStorage.setItem(KIOSK_KEY, "1");
      else localStorage.removeItem(KIOSK_KEY);
    } catch (err) {
      devLog("[kiosk] storage failed:", err);
    }
    if (!enabled) {
      setGated(false);
      clearGateTimers();
      return undefined;
    }

    // ---- Browser navigation-away guard ----
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        "Kiosk mode is active. Tap the lock icon and enter the admin PIN to exit.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    // ---- Escape-detection: fullscreen drop (debounced 5s) ----
    //
    // Android's camera intent + native print dialogs briefly drop
    // fullscreen and return within seconds — we only gate when the
    // exit persists past the debounce window. Call-sites can also
    // `kiosk.suspend(ms)` to opt into a longer grace period.
    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        if (fsTimerRef.current) {
          clearTimeout(fsTimerRef.current);
          fsTimerRef.current = null;
        }
        return;
      }
      if (isSuspended()) return;
      if (fsTimerRef.current) clearTimeout(fsTimerRef.current);
      fsTimerRef.current = setTimeout(() => {
        fsTimerRef.current = null;
        if (!document.fullscreenElement && !isSuspended()) setGated(true);
      }, GATE_DEBOUNCE_MS);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);

    // ---- Escape-detection: tab hidden (debounced 5s) ----
    //
    // Fires only when the tab remains hidden past the debounce
    // window. Coming back visible cancels the pending gate.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (visTimerRef.current) {
          clearTimeout(visTimerRef.current);
          visTimerRef.current = null;
        }
        return;
      }
      if (isSuspended()) return;
      if (visTimerRef.current) clearTimeout(visTimerRef.current);
      visTimerRef.current = setTimeout(() => {
        visTimerRef.current = null;
        if (document.visibilityState !== "visible" && !isSuspended()) {
          setGated(true);
        }
      }, GATE_DEBOUNCE_MS);
    };
    document.addEventListener("visibilitychange", onVisibility);

    // NOTE: we intentionally *don't* listen for window `blur` any more —
    // it's too noisy on tablets (soft keyboard, split-view, share-sheet,
    // etc. all fire blur without the operator actually leaving the app).
    // fullscreenchange + visibilitychange are the reliable signals.

    // ---- Wake Lock so screen stays on during the shift ----
    const acquireWakeLock = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
          wakeLockRef.current.addEventListener?.("release", () => {
            wakeLockRef.current = null;
          });
        }
      } catch (err) {
        devLog("[kiosk] wake-lock failed:", err);
      }
    };
    acquireWakeLock();
    // Re-acquire whenever the tab is foregrounded again.
    const reacquire = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", reacquire);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("visibilitychange", reacquire);
      clearGateTimers();
      try {
        wakeLockRef.current?.release?.();
      } catch (err) {
        devLog("[kiosk] wake-lock release failed:", err);
      }
      wakeLockRef.current = null;
    };
  }, [enabled]);

  const requestFullscreen = useCallback(async () => {
    try {
      const el = document.documentElement;
      // Some browsers (Safari) need `webkitRequestFullscreen`.
      if (!document.fullscreenElement && (el.requestFullscreen || el.webkitRequestFullscreen)) {
        const fn = el.requestFullscreen || el.webkitRequestFullscreen;
        await fn.call(el, { navigationUI: "hide" });
      }
    } catch (err) {
      devLog("[kiosk] fullscreen request failed:", err);
    }
  }, []);

  const enter = useCallback(async () => {
    setEnabled(true);
    await requestFullscreen();
    toast.success("Kiosk mode on — admin PIN required to exit");
  }, [requestFullscreen]);

  const resumeFromGate = useCallback(async () => {
    // User tapped "Resume kiosk" on the gate. Re-enter fullscreen
    // (which only works inside this user-gesture path) and drop the
    // gate. We do NOT touch `enabled` — kiosk stays on.
    await requestFullscreen();
    setGated(false);
  }, [requestFullscreen]);

  const requestUnlock = useCallback(() => {
    setError("");
    setPin("");
    setUnlockOpen(true);
  }, []);

  // Verify against the supervisor-pin endpoint — re-using the
  // existing single-admin enforcement (only an admin's PIN unlocks).
  const submitUnlock = useCallback(async () => {
    if (!pin || pin.length < 4) {
      setError("Enter the admin PIN");
      return;
    }
    setUnlocking(true);
    try {
      await api.post("/auth/verify-supervisor-pin", { pin });
      setEnabled(false);
      setGated(false);
      setUnlockOpen(false);
      setPin("");
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
      } catch (err) {
        devLog("[kiosk] exit-fullscreen failed:", err);
      }
      toast.success("Kiosk mode off");
    } catch (err) {
      setError(err?.response?.data?.detail || "Wrong PIN");
    } finally {
      setUnlocking(false);
    }
  }, [pin]);

  const value = { enabled, gated, enter, requestUnlock, suspend };

  return (
    <KioskCtx.Provider value={value}>
      {children}

      {/* Persistent floating "lock" badge — only visible while kiosk
          is on. Tap to trigger the admin-PIN unlock. */}
      {enabled && !gated && (
        <button
          type="button"
          onClick={requestUnlock}
          data-testid="kiosk-badge"
          className="fixed bottom-4 right-4 z-40 h-12 w-12 rounded-full bg-amber-600 text-white shadow-lg flex items-center justify-center hover:bg-amber-700 active:scale-95 transition"
          aria-label="Exit kiosk mode (admin PIN)"
          title="Exit kiosk mode (admin PIN)"
        >
          <Lock className="h-5 w-5" />
        </button>
      )}

      {/* Escape-attempt blocking gate.
          Raised the moment fullscreen drops OR the tab loses focus
          while kiosk is active. Completely covers the app underneath
          so the operator cannot interact with anything until they
          either resume kiosk OR an admin enters their PIN. */}
      {enabled && gated && (
        <div
          // pointer-events:auto guarantees we eat all taps so the app
          // behind us is unreachable. role=alertdialog so screen
          // readers announce it as a modal.
          role="alertdialog"
          aria-modal="true"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-amber-900/95 p-6 backdrop-blur-sm text-white"
          data-testid="kiosk-gate"
        >
          <div className="max-w-sm w-full text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-white/15 flex items-center justify-center mb-4">
              <ShieldAlert className="h-8 w-8" />
            </div>
            <div className="font-display font-bold text-2xl mb-2">Kiosk mode locked</div>
            <p className="text-sm text-amber-100 mb-6">
              You left the kiosk view. The app is held until you resume
              kiosk mode <strong>or</strong> an admin enters their PIN.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={resumeFromGate}
                data-testid="kiosk-gate-resume"
                className="w-full h-14 rounded-xl bg-white text-amber-900 font-semibold text-lg flex items-center justify-center gap-2 active:scale-[0.99] transition"
              >
                <Maximize className="h-5 w-5" /> Resume kiosk
              </button>
              <button
                type="button"
                onClick={() => {
                  setUnlockOpen(true);
                  setPin("");
                  setError("");
                }}
                data-testid="kiosk-gate-unlock"
                className="w-full h-12 rounded-xl border border-white/40 text-white font-medium text-sm hover:bg-white/10 active:scale-[0.99] transition"
              >
                Exit kiosk (admin PIN)
              </button>
            </div>
            <div className="text-[11px] text-amber-200 mt-6 leading-relaxed">
              Tip: for a fully tamper-proof lockdown that also blocks
              the Android swipe-up, pair this with the device&apos;s
              built-in <em>App Pinning</em> (Settings → Security → Pin
              app).
            </div>
          </div>
        </div>
      )}

      {/* Admin-PIN unlock dialog (used by both the floating badge and
          the gate's "Exit kiosk" link). Plain overlay (not shadcn
          Dialog) so it renders above the fullscreen + gate layers. */}
      {unlockOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          data-testid="kiosk-unlock-overlay"
        >
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="h-5 w-5 text-amber-700" />
              <div className="font-display font-bold text-lg">Exit kiosk mode</div>
            </div>
            <p className="text-sm text-stone-600 mb-4">
              Enter the <strong>admin</strong> PIN to leave kiosk mode.
              Staff PINs will be rejected.
            </p>
            <Input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              maxLength={8}
              className="h-12 text-center tracking-widest text-lg"
              placeholder="Admin PIN"
              data-testid="kiosk-unlock-pin"
              onKeyDown={(e) => {
                if (e.key === "Enter") submitUnlock();
              }}
            />
            {error && (
              <div className="text-sm text-rose-600 mt-2" data-testid="kiosk-unlock-error">
                {error}
              </div>
            )}
            <div className="flex gap-2 mt-4 justify-end">
              <Button
                variant="ghost"
                onClick={() => setUnlockOpen(false)}
                data-testid="kiosk-unlock-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitUnlock}
                disabled={unlocking}
                data-testid="kiosk-unlock-submit"
              >
                {unlocking ? "Checking…" : "Unlock"}
              </Button>
            </div>
            <div className="text-[11px] text-stone-500 mt-4">
              Note: web-based kiosk can&apos;t prevent force-quit of Chrome
              or pull-down of the Android notification shade. For
              hardened lockdown, use Android&apos;s Pinned-App or a kiosk
              launcher on the tablet.
            </div>
          </div>
        </div>
      )}
    </KioskCtx.Provider>
  );
}

export const useKiosk = () => useContext(KioskCtx);
